import React, { useState, useCallback, useEffect } from "react";
import { useInfiniteHits } from "react-instantsearch-core";
import { KYDAdminEventSummary } from "@common/interfaces/KYDEvent";
import { KYDAdminFeed } from "@common/interfaces/KYDOrganization";
import BundleEventsTable from "./BundleEventsTable";
import { FiPlus } from "react-icons/fi";
import { HStack, Text, Button, Spacer, Flex } from "@chakra-ui/react";
import actions from "../../../actions";
import { useDialogWithOptions } from "../../../Common/Dialog";
import { UpdateBundleEventsDialog } from "../UpdateBundleEventsDialog";

interface BundleItemsProps {
  eventsInBundle: KYDAdminEventSummary[];
  setEventsInBundle: React.Dispatch<
    React.SetStateAction<KYDAdminEventSummary[]>
  >;
  onUpdateBundle: ({ events }: { events: string[] }) => Promise<void>;
}

const BundleItems: React.FC<BundleItemsProps> = ({
  eventsInBundle,
  setEventsInBundle,
  onUpdateBundle,
}) => {
  const [isEditingEvents, setIsEditingEvents] = useState(true);
  const [isMutatingBundleItems, setIsMutatingBundleItems] = useState(false);
  const [showAddEvents, setShowAddEvents] = useState(false);
  const {
    items: events,
    isLastPage,
    showMore,
  } = useInfiniteHits<KYDAdminEventSummary>({
    escapeHTML: false,
    showPrevious: false,
  });

  const [externalEvents, setExternalEvents] = useState<
    KYDAdminFeed["external_events"]
  >([]);

  const [loading, updateLoading] = useState(false);

  const dialog = useDialogWithOptions();

  const fetchExternalEvents = useCallback(async () => {
    try {
      updateLoading(true);
      const bio = await actions.ADMIN.fetchBio();
      setExternalEvents(bio.external_events);
    } catch (e) {
      dialog.open({
        text: e.message,
      });
    }
    updateLoading(false);
  }, []);

  useEffect(() => {
    fetchExternalEvents();
  }, [fetchExternalEvents]);

  return (
    <>
      <Flex align="center" mb={4}>
        <Text fontSize={"lg"} fontWeight={"bold"}>
          Events
        </Text>
        <Spacer />
        <HStack spacing={3}>
          <Button
            leftIcon={<FiPlus />}
            onClick={() => setShowAddEvents(true)}
            isDisabled={isMutatingBundleItems}
            colorScheme="blue"
            backgroundColor={"black"}
            color={"white"}
            _hover={{
              backgroundColor: "black",
              opacity: 0.8,
            }}
          >
            Add
          </Button>
        </HStack>
      </Flex>

      <BundleEventsTable
        onUpdateBundle={onUpdateBundle}
        eventsInBundle={eventsInBundle}
        isEditingEvents={isEditingEvents}
        loading={loading}
        setEventsInBundle={setEventsInBundle}
        setIsEditingEvents={setIsEditingEvents}
        isMutatingBundleItems={isMutatingBundleItems}
        setIsMutatingBundleItems={setIsMutatingBundleItems}
      />

      <UpdateBundleEventsDialog
        isOpen={showAddEvents}
        onClose={() => setShowAddEvents(false)}
        eventsInBundle={eventsInBundle}
        events={events}
        isLastPage={isLastPage}
        showMore={showMore}
        externalEvents={externalEvents}
        onUpdateBundle={onUpdateBundle}
      />
    </>
  );
};

export default BundleItems;
